import { type ReactElement } from 'react';

import { ModalShell } from '@/components/ui/ModalShell';
import { stageKindLabel } from '@/lib/stage-kind';

import { formatHours } from '../presentation';
import { workLabel } from '../standards-form';
import type { StandardsRow } from '../types';
import { useStandardHistory } from '../use-standards';
import { formatDateTime } from '@/lib/time';
import { skeletonLine } from '@/components/ui/skeleton';

// Historial de versiones de un tipo de trabajo (so=420, spec §4.2; so=340): la más
// reciente primero, con su vigencia, motivo y quién la ajustó; paginado con "Ver más".

interface Props {
  row: StandardsRow | null;
  companyId: string;
  onClose: () => void;
}

export function StandardHistoryModal({ row, companyId, onClose }: Props): ReactElement | null {
  const work = row === null ? null : { documentTypeId: row.documentTypeId, flowKind: row.flowKind };
  const query = useStandardHistory(work, companyId);

  if (row === null) {
    return null;
  }
  const items = query.data?.pages.flatMap((p) => p.items) ?? [];
  return (
    <ModalShell
      open
      title={`Historial de ${workLabel(row.documentTypeName, row.flowKind)}`}
      onClose={onClose}
      footer={
        <>
          {query.hasNextPage && (
            <button
              type="button"
              className="btn mut sm"
              disabled={query.isFetchingNextPage}
              onClick={() => void query.fetchNextPage()}
            >
              {query.isFetchingNextPage ? 'Cargando…' : 'Ver más'}
            </button>
          )}
          <button type="button" className="btn sm" onClick={onClose}>
            Cerrar
          </button>
        </>
      }
    >
      <div className="modal-body">
        {query.isError ? (
          <div role="alert">
            <p className="state-msg">No se pudo cargar el historial.</p>
            <button type="button" className="btn mut sm" onClick={() => void query.refetch()}>
              Reintentar
            </button>
          </div>
        ) : query.data === undefined ? (
          <span className={skeletonLine({ height: 'sm', width: 70 })} aria-busy="true" />
        ) : items.length === 0 ? (
          <p className="soft">Este tipo de trabajo todavía no tiene versiones.</p>
        ) : (
          <ul className="rlist histlist">
            {items.map((v) => (
              <li key={v.versionId}>
                <div className="nm">
                  {stageKindLabel(v.stageKind)} ·{' '}
                  {v.standardMinutes === null
                    ? 'Sin estándar'
                    : formatHours(v.standardMinutes * 60)}
                </div>
                <div className="rl">
                  Desde {formatDateTime(v.effectiveFrom)} ·{' '}
                  {v.createdByName ?? 'Usuario sin nombre'}
                </div>
                <div className="sub">{v.reason}</div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </ModalShell>
  );
}
